import { useEffect, useRef, useState } from "react";
import type { PDFDocumentProxy, RenderTask } from "pdfjs-dist";
import { PdfAnnotationLayer } from "../annotations/PdfAnnotationLayer";
import type { PdfAnnotation } from "../annotations/types";
import { PdfSearchHighlightLayer } from "./PdfSearchHighlightLayer";
import { PdfTextLayer } from "./PdfTextLayer";
import type { PdfSearchMatchRect } from "../hooks/usePdfSearchWithRects";

interface PdfPageCanvasProps {
  pdf: PDFDocumentProxy | null;
  pageNumber: number;
  zoom: number;
  rotation: number;
  darkMode: boolean;
  pageRef?: HTMLDivElement | null;
  annotations?: PdfAnnotation[];
  onAnnotationClick?: (
    annotation: PdfAnnotation,
    anchorRect: DOMRect,
    pageNumber: number
  ) => void;
  searchMatches?: PdfSearchMatchRect[];
  activeSearchMatch?: PdfSearchMatchRect | null;
}

export function PdfPageCanvas({
  pdf,
  pageNumber,
  zoom,
  rotation,
  darkMode,
  pageRef,
  annotations = [],
  onAnnotationClick,
  searchMatches = [],
  activeSearchMatch = null,
}: PdfPageCanvasProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const pageInnerRef = useRef<HTMLDivElement>(null);

  const [rendering, setRendering] = useState(false);
  const [canvasSize, setCanvasSize] = useState({ width: 0, height: 0 });

  useEffect(() => {
    if (!pdf || !canvasRef.current) return;

    let renderTask: RenderTask | null = null;
    let cancelled = false;

    async function renderPage() {
      try {
        setRendering(true);

        const page = await pdf!.getPage(pageNumber);
        if (cancelled) return;

        const viewport = page.getViewport({ scale: zoom, rotation });

        const canvas = canvasRef.current;
        if (!canvas) return;

        const context = canvas.getContext("2d");
        if (!context) return;

        const outputScale = window.devicePixelRatio || 1;

        canvas.width = viewport.width * outputScale;
        canvas.height = viewport.height * outputScale;

        canvas.style.width = `${viewport.width}px`;
        canvas.style.height = `${viewport.height}px`;

        setCanvasSize({
          width: viewport.width,
          height: viewport.height,
        });

        context.setTransform(outputScale, 0, 0, outputScale, 0, 0);

        renderTask = page.render({
          canvasContext: context,
          viewport,
        });

        await renderTask.promise.catch(() => {});
        if (!cancelled) setRendering(false);
      } catch (err) {
        console.error("PDF render error:", err);
        if (!cancelled) setRendering(false);
      }
    }

    renderPage();

    return () => {
      cancelled = true;
      renderTask?.cancel();
    };
  }, [pdf, pageNumber, zoom, rotation]);

  // Calculate anchor rect from annotation's normalized rects
  function getAnnotationAnchorRect(annotation: PdfAnnotation): DOMRect | null {
    if (!pageInnerRef.current || annotation.rects.length === 0) return null;

    const pageRect = pageInnerRef.current.getBoundingClientRect();
    const firstRect = annotation.rects[0];

    // Convert normalized (0-1) coords to absolute pixels
    const x = pageRect.left + firstRect.x * canvasSize.width;
    const y = pageRect.top + firstRect.y * canvasSize.height;
    const width = firstRect.w * canvasSize.width;
    const height = firstRect.h * canvasSize.height;

    return new DOMRect(x, y, width, height);
  }

  return (
    <div
      className={`flex w-full justify-center py-4 ${
        darkMode ? "bg-muted" : "bg-muted/30"
      }`}
    >
      <div ref={pageInnerRef} className="relative">
        <canvas
          ref={canvasRef}
          className={`shadow-lg rounded-md ${
            darkMode
              ? "pdf-canvas-dark bg-muted"
              : "pdf-canvas-light bg-card"
          }`}
          style={{
            opacity: rendering ? 0.7 : 1,
            transition: "opacity 0.15s",
          }}
        />

        {canvasSize.width > 0 && canvasSize.height > 0 && pdf && (
          <>
            {/* Text layer for selection */}
            <PdfTextLayer
              pdf={pdf}
              pageNumber={pageNumber}
              zoom={zoom}
              rotation={rotation}
            />
            
            {/* Search highlight layer */}
            <PdfSearchHighlightLayer
              matches={searchMatches}
              activeMatch={activeSearchMatch}
              zoom={zoom}
            />
            
            {/* Annotation layer */}
            <PdfAnnotationLayer
              pageWidth={canvasSize.width}
              pageHeight={canvasSize.height}
              annotations={annotations}
              onOpen={(annotation) => {
                const rect = getAnnotationAnchorRect(annotation);
                if (rect) {
                  onAnnotationClick?.(annotation, rect, pageNumber);
                }
              }}
            />
          </>
        )}
      </div>
    </div>
  );
}
